import { html } from 'lit';

import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/relative-time/relative-time.js';

import { ViewBase } from '../view-base.js';
import { contributorUpgradeViewStyles } from './contributor-upgrade-view-styles.js';

import { Q1SpinnerPanel } from '../../components/q1-spinner-panel/q1-spinner-panel.js';
import { AgreementManager } from '../../modules/agreement-manager.js';
import { Q1TutorialList } from '../../components/q1-tutorial-list/q1-tutorial-list.js';

import { q1MemberFunctions } from '../../modules/q1-core-functions.js';

const CONTRIBUTOR_UPGRADE_IN_PROGRESS_FLAG = 'contributor-upgrade-in-progress';
const CONTRIBUTOR_UPGRADE_WELCOME_FLAG = 'contributor-upgrade-welcome';
const CONTRIBUTOR_UPGRADE_AGREEMENT_TYPE = 'q1-membership-contributor';

const NEW_CONTRIBUTOR_THRESHOLD = 60; // days

const TAG_GROUP = 'contributors'; 

const PAGE_TITLE_BY_VIEW_MODE = {
  'empty': 'No Member Found',
  'not-started': 'Upgrade to Contributor',
  'in-progress-step1': 'Contributor Upgrade in Progress',
  'in-progress-step2': 'Contributor Upgrade in Progress',
  'completed-recent': 'Welcome to Contributor Membership!',
  'completed-legacy': 'Contributor Upgrade Hub',
  'foundation': 'Contributor Upgrade Hub',
};
const JOIN_SLACK_LINK =
  'https://join.slack.com/t/q1-is/shared_invite/zt-2xrcbblwj-3SvWUFFNk_NwlUo9KFB8Ug';

export class ContributorUpgradeView extends ViewBase {
  static styles = [
    ...super.styles,
    contributorUpgradeViewStyles,
  ];

  static properties = {
    ...super.properties,
    
    /**
     * View Modes:
     * - 'empty': Default mode
     * - 'not-started': Member has not started the upgrade process
     * - 'in-progress-step1': Member has started the upgrade process but not signed the agreement
     * - 'in-progress-step2': Member has signed the agreement but not joined the slack
     * - 'completed-recent': Member has completed the upgrade process within the last 60 days
     * - 'completed-legacy': Member has completed the upgrade process more than 60 days ago
     * - 'foundation': Member is a foundation or steward member
     */
    viewMode: { type: String },
    pageTitle: { type: String },
    loading: { type: Boolean },

    agreementManager: { type: Object },

    tutorialManager: { type: Object },
    tutorials: { type: Array },
  };

  constructor() {
    super();

    this.viewMode = 'empty';
    this.pageTitle = '';
    this.loading = true;

    this.agreementManager = null;

    this.tutorialManager = null;
    this.tutorials = [];
  }

  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();
    
    this.tutorialManager = this.appElement.tutorialManager;
    this.tutorialManager.loadingComplete.then(() => {
      this._rebuildRequiredTutorials();
    }).catch((error) => {
      console.log('Error loading tutorials:', error);
    });

    this.appElement.refreshCurrentMember().then((currentMember) => {
      this._setViewMode();
      this.agreementManager = new AgreementManager(currentMember);
      this.setAfLink_db('members', currentMember.id);
    }).catch((error) => {
      console.error('Error refreshing current member:', error);
    }).finally(() => {
      this.loading = false;
    });
  }

  _rebuildRequiredTutorials() {
    const tagGroup = this.tutorialManager.getTutorialsByTag(TAG_GROUP);
    this.tutorials = tagGroup.tutorials;
  }

  _setViewMode() {
    if (!this.currentMember) {
      console.error('No current member found');
      this.viewMode = 'empty';
      return;
    }

    const { removeCurrentMemberFlag } = q1MemberFunctions;
    const { flags, agTypeSubKeys, level, membershipTimestamps } = this.currentMember;

    if (['steward', 'foundation'].includes(level)) {
      this.viewMode = 'foundation';
    } else if (level === 'contributor') {
      if (
        !this.daysSinceContributorJoin
        || (this.daysSinceContributorJoin > NEW_CONTRIBUTOR_THRESHOLD)
      ) {
        this.viewMode = 'completed-legacy';
      } else {
        this.viewMode = 'completed-recent';
      }

      // Remove the welcome flag (no need to await)
      if (flags?.includes(CONTRIBUTOR_UPGRADE_WELCOME_FLAG))
        removeCurrentMemberFlag({ flag: CONTRIBUTOR_UPGRADE_WELCOME_FLAG });
    } else if (flags?.includes(CONTRIBUTOR_UPGRADE_IN_PROGRESS_FLAG)) {
      this.viewMode = agTypeSubKeys?.includes(CONTRIBUTOR_UPGRADE_AGREEMENT_TYPE)
        ? 'in-progress-step2'
        : 'in-progress-step1';
    } else {
      this.viewMode = 'not-started';
    }

    this.pageTitle = PAGE_TITLE_BY_VIEW_MODE[this.viewMode];
  }

  get daysSinceContributorJoin() {
    const contributorJoinedAt = this.currentMember?.membershipTimestamps?.contributorJoinedAt;
    if (!contributorJoinedAt) return null;

    const contributorJoinDate = new Date(contributorJoinedAt);
    const daysSinceContributorJoin = (new Date() - contributorJoinDate) / (1000 * 60 * 60 * 24);

    return Math.ceil(daysSinceContributorJoin);
  }

  /** Returns ISO string for either contributor joined at or created at. */
  get approxContributorJoinDateString() {
    const contributorJoinedAt = this.currentMember?.membershipTimestamps?.contributorJoinedAt;
    const createdAt = this.currentMember?.createdAt;
    
    if (!contributorJoinedAt && !createdAt) return '';

    return (contributorJoinedAt || createdAt).toISOString();
  }

  handleTrackView(event) {
    const tutorialKey = event.detail.tutorialKey;

    this.tutorialManager.trackTutorialView(tutorialKey);
  }

  handleMarkCompleted(event) {
    const tutorialKey = event.detail.tutorialKey;

    this.tutorialManager
      .markTutorialCompleted(tutorialKey).then(() => {
        // Tutorial Manager has already updated the tutorial, we just need to re-fire the lit 
        // setter so that the q1-tutorials-list is refreshed.
        this._rebuildRequiredTutorials();
        
        this.appElement.refreshCurrentMember(); // update the app header
        
        this.showToast('Tutorial marked as completed.', null, 'success');
      }).catch((error) => {
        console.log('Error marking tutorial complete: ', error);
        this.showToast(error.message, 'Error Marking Tutorial Completed.', 'danger');
        this.loading = false;
      });
  }

  async generateAgreement() {
    if (this.viewMode !== 'not-started') {
      console.error('Cannot start a new agreement in current view mode:', this.viewMode);
      return;
    }

    await this._getOrCreateAgreement();
  }

  async returnToAgreement() {
    if (this.viewMode !== 'in-progress-step1') {
      console.error('Cannot return to agreement in current view mode:', this.viewMode);
      return;
    }

    await this._getOrCreateAgreement();
  }

  async _getOrCreateAgreement() {
    if (!['not-started', 'in-progress-step1'].includes(this.viewMode)) return; // Just for safety
    
    if (!this.agreementManager.ready) {
      this.showToast('Agreement manager not ready', 'Error loading agreement', 'danger');
      return;
    }

    this.loading = true;

    try {
      const agreementId = await this.agreementManager.startContributorUpgrade();

      this.showToast('Contributor agreement ready to review and sign...', null, 'success');
      this.navigateTo(`/agreements/${agreementId}`);
    } catch (error) {
      console.error('Error:', error);
      this.showToast(error.message, 'Failed to load agreement', 'danger');
    } finally {
      this.loading = false;
    }
  }

  get contentTemplate() {
    return html`
      <header-bar class="with-border">
        <h1>
          <span class="prefix">Quorum1 Membership</span>
          ${this.viewMode === 'completed-recent' ? '🎉' : null}
          ${this.pageTitle}
        </h1>

        <sl-icon class="header-icon" library="vectors" name="innovation"></sl-icon>
      </header-bar>

      ${this.contentBodyTemplate}

      <q1-spinner-panel
        ?open=${this.loading}
        label="Processing..."
      ></q1-spinner-panel>
    `;
  }

  get contentBodyTemplate() {
    switch (this.viewMode) {
      case 'not-started':
        return this.notStartedTemplate;
      case 'in-progress-step1':
        return this.inProgressStep1Template;
      case 'in-progress-step2':
        return this.inProgressStep2Template;
      case 'completed-recent':
        return this.completedRecentTemplate;
      case 'completed-legacy':
        return this.completedNonRecentTemplate;
      case 'foundation':
        return this.foundationTemplate;
      default:
        return this.emptyTemplate;
    }
  }

  get emptyTemplate() {
    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="sentiment_dissatisfied"></sl-icon>

          <h2>Nothing to see here...</h2>

          <p>
            Something went wrong and we couldn't find your membership information.
          </p>
        </div>
      </notice-panel>
    `;
  }

  get notStartedTemplate() {
    return html`
      ${this.processWorkflowTemplate}

      <p class="subtitle hero primary">
        The contributor level is about active collaboration and work. You can join, create, 
        invest or advise on projects and create/lead guilds.
      </p>
      
      <p>
        <strong>As a contributor you can:</strong>
        Create projects & guilds, contribute work & earn compensation,
        participate in Quorum1 governance, and more.
      </p>

      <p>
        The first step is signing the contributor agreement. This is an IP neutral agreement
        that supports our polycentric legal framework. It provides mutual protections and 
        serves as the base agreement on top of which project and guild specific agreements can
        build.
      </p>

      <p>
        Click the button below to generate your contributor agreement.
      </p>

      <button-bar>
        <sl-button
          variant="primary"
          size="large"
          @click=${this.generateAgreement}
        >
          <sl-icon slot="prefix" library="material" name="description"></sl-icon>
          Generate Contributor Agreement
        </sl-button>
      </button-bar>
    `;
  }

  get inProgressStep1Template() {
    return html`
      ${this.processWorkflowTemplate}

      <p class="subtitle hero primary">
        You've started the process by generating your contributor agreement.
        The next step is to review & sign the agreement.
      </p>

      <p>
        The contributor agreement is <strong>IP neutral</strong> and designed to support our 
        polycentric legal framework. It provides mutual protections and serves as the base 
        agreement on top of which project and guild specific agreements can build.
      </p>

      <p>
        If you're interested in learning more about Quorum1's governance structure,
        it's hosted in an open source Github repository that we call the 
        <a href="https://github.com/quorum1/governance" target="_blank"
        >Quorum1 Governance Repo</a>.
      </p>
      
      <p>
        We are constantly working to iterate and improve the governance structure.
        If you have any questions, or if you would like to contribute to this ongoing effort,
        please reach out to the foundation team.
      </p>
      
      <button-bar>
        <sl-button
          variant="primary"
          size="large"
          @click=${this.returnToAgreement}
        >
          <sl-icon slot="prefix" library="material" name="description"></sl-icon>
          Review Contributor Agreement
        </sl-button>
      </button-bar>
    `;
  }

  get inProgressStep2Template() {
    return html`
      ${this.processWorkflowTemplate}

      <p class="subtitle hero primary">
        Now that you've signed the contributor agreement, the last step to becoming a contributor
        is joining the q1 Network Slack.
      </p>

      <p>
        Projects & guilds are able to use any tool stack and communications platforms they prefer
        (Discord, Mattermost, WhatsApp, Telegram, etc).
        However, Slack acts as a unifying hub of operational coordination and communication.
        So it's important for all contributor members to get their accounts setup.
      </p>

      <p>
        <strong>What does Quorum1 use Slack for?</strong>
        Quorum1 uses Slack not only for its chat functionality, but also for its robust workflow 
        automation tools, advanced features like 
        <a href="https://www.youtube.com/watch?v=meqBTV3yDkg" target="_blank">lists</a> & 
        <a href="https://www.youtube.com/watch?v=xSZqbdMet0I" target="_blank">canvases</a>,
        its deep integration with other tools,
        and as a hub for operational coordination across the network.        
      </p>

      <p>
        Quorum1 hosts a shared 
        <a href="https://slack.com/resources/why-use-slack/slack-enterprise-grid" target="_blank"
        >Enterprise Grid Slack Org</a>,
        which means that we can support the highest levels of data privacy & compliance 
        (HIPAA, GDPR, FINRA, etc). It also means that we can spin up custom workspaces for 
        individual projects & guilds as needed.
      </p>

      <p>
        To start, you'll be joining our "q1 Network" Slack instance, which uses the free version
        of Slack but is linked into the grid instances (using shared channels) in order to utilize
        some of the advanced functionality outlined above.
      </p>
      
      <button-bar>
        <sl-button
          variant="warning"
          size="large"
          href=${JOIN_SLACK_LINK}
          target="_blank"
        >
          <sl-icon slot="prefix" library="tabler" name="brand-slack"></sl-icon>
          Join q1 Network Slack
        </sl-button>
      </button-bar>
    `;
  }

  get completedRecentTemplate() {
    return html`
      ${this.processWorkflowTemplate}

      <p class="subtitle hero primary">
        You recently upgraded to the contributor level of membership, welcome! 
        Here's what to do next.
      </p>

      ${this.contributorNextStepsTemplate}
    `;
  }

  get completedNonRecentTemplate() {
    return html`
      <p class="subtitle hero primary">
        You upgraded to the contributor level of membership 
        <sl-relative-time date=${this.approxContributorJoinDateString}></sl-relative-time>.
        So this page is a bit less relevant for you. But there are still some 
        resources below that might be useful.
      </p>
      
      ${this.contributorNextStepsTemplate}
    `;
  }

  get foundationTemplate() {
    return html`
      <p class="subtitle hero primary">
        This page isn't super relevant for you, it's for folks who are orienting around the 
        contributor level of membership. Feel free to share this with other folks though!
      </p>

      ${this.contributorNextStepsTemplate}
    `;
  }

  get processWorkflowTemplate() {
    // IMPROVEME: This could be cleaner
    const steps = [
      { label: 'Get Started', variant: 'neutral' },
      { label: 'Sign Agreement', variant: 'neutral' },
      { label: 'Setup Slack', variant: 'neutral' },
      { label: 'Orient', variant: 'neutral' },
    ];
    if (this.viewMode === 'not-started') {
      steps[0].variant = 'primary';
    }
    if (this.viewMode === 'in-progress-step1') {
      steps[0].variant = 'success';
      steps[1].variant = 'primary';
    }
    if (this.viewMode === 'in-progress-step2') {
      steps[0].variant = 'success';
      steps[1].variant = 'success';
      steps[2].variant = 'primary';
    }
    if (this.viewMode === 'completed-recent') {
      steps[0].variant = 'success';
      steps[1].variant = 'success';
      steps[2].variant = 'success';
      steps[3].variant = 'primary';
    }

    return html`
      <tag-workflow>
        <span class="title">
          <span class="hide-until-bp3">Contributor Upgrade</span>
          Process:
        </span>

        ${steps.map((step, index) => html`
          <sl-tag variant=${step.variant}>${step.label}</sl-tag>
          ${index === (steps.length - 1) ? null : html`
            <sl-icon library="material" name="arrow_forward"></sl-icon>
          `}
        `)}
      </tag-workflow>
    `;
  }

  get contributorNextStepsTemplate() {
    return html`
      <two-column-container>
        <div class="left">
          <p>
            <em>
              Below are some optional tutorials that are relevant for you as a new contributor.
            </em>
          </p>

          <q1-tutorial-list
            title="Contributor Tutorials"
            .tutorials=${this.tutorials}
            @track-view=${this.handleTrackView}
            @mark-completed=${this.handleMarkCompleted}
          ></q1-tutorial-list>
        </div>

        <div class="right">
          <p>
            <em>
              And here are some other ways to start digging into your new contributor abilities.
            </em>
          </p>

          <sl-divider></sl-divider>

          <p>
            <strong>Participate in a q1 Network event.</strong><br/>
            The best way to start weaving in is to join an online event 
            and start connecting with other members.  
          </p>
          
          <sl-button
            variant="warning"
            size="small"
            href="https://lu.ma/q1-network"
            target="_blank"
          >
            <sl-icon slot="prefix" library="material" name="calendar_month"></sl-icon>
            View Luma Calendar
          </sl-button>

          <sl-divider></sl-divider>

          <p>
            <strong>Explore the q1 Network Slack.</strong><br/>
            Here are some channels to follow to get updates from across the entire network.
          </p>  

          <!-- FIXME - These need to be linked. -->
          <ul>
            <li>
              <strong>#g-q1-network</strong> - Weekly updates from the foundation team.
            </li>
            <li>
              <strong>#g-q1-projects</strong> - Updates from all projects.
            </li>
            <li>
              <strong>#g-q1-guilds</strong> - Updates from all guilds.
            </li>
          </ul>
        </div>
      </two-column-container>
    `;
  }
}

customElements.define('contributor-upgrade-view', ContributorUpgradeView);